import type { ChannelData, ChannelFilters, StreamChat } from 'stream-chat';

// Must match the backend's stackid.MetadataKey.
export const STACK_ID_KEY = 'localstackStackId';

const SCOPED = Symbol('chatStackScoped');

type ScopeMarker = Record<symbol, boolean>;

// Several stacks can share one GetStream app and portal id, so every channel
// query must be restricted to the stack's own channels. Wrapping the single
// client keeps that out of each call site; an empty id leaves it untouched.
export function scopeChatClientToStack<T extends StreamChat>(
  client: T,
  stackId?: string,
): T {
  const id = stackId?.trim();
  if (!id || (client as unknown as ScopeMarker)[SCOPED]) {
    return client;
  }
  (client as unknown as ScopeMarker)[SCOPED] = true;

  const queryChannels = client.queryChannels.bind(client);
  client.queryChannels = (filter, ...rest) =>
    queryChannels(
      { ...filter, [STACK_ID_KEY]: id } as unknown as ChannelFilters,
      ...rest,
    );

  const channel = client.channel.bind(client);
  // Channels created in the browser need the tag so scoped queries can find
  // them; looking up an existing channel passes no data and stays as is.
  client.channel = ((type: string, idOrData?: unknown, data?: object) => {
    if (data) {
      return channel(
        type,
        idOrData as string,
        {
          ...data,
          [STACK_ID_KEY]: id,
        } as unknown as ChannelData,
      );
    }
    return channel(type, idOrData as string);
  }) as typeof client.channel;

  return client;
}
